import React, { useContext } from 'react';

import {
  Menu,
  MenuItem,
  MoreVertIcon,
  ButtonGroup,
  ArticleIcon,
  Badge,
  AddIcon,
  Button,
  Divider,
} from '@gogame/components';
import Permissions from '@gogame/permissions';

import { useCurrentUser } from '../../Auth';
import { PresetsContext } from './PresetsContext';
import {
  ACTION_DELETE_PRESET,
  ACTION_DUPLICATE_PRESET,
  ACTION_RENAME_PRESET,
  ACTION_RESET_PRESET,
  ACTION_SHARE_PRESET,
  ACTION_UNSHARE_PRESET,
  ACTION_UPDATE_PRESET,
} from './components/PresetActions/constants';
import { useCreatePreset } from './hooks/useCreatePreset';
import { usePresetPermissions } from './hooks/usePresetPermissions';

const PresetsPreviewButton = ({ onOpenPresetsList }) => {
  const {
    activePreset,
    isChanged,
    onUpdatePreset,
    onRenamePreset,
    onDeletePreset,
    onSharePreset,
    onUnsharePreset,
    onResetPreset,
    presetKey,
    isAddPresetWithGroup,
    screenData,
    titleDialogAdd,
  } = useContext(PresetsContext);
  const user = useCurrentUser();

  const actions = usePresetPermissions({
    isActive: !!activePreset,
    isChanged,
    isPublic: activePreset?.isPublic,
    isOwner: activePreset?.user?.id === user.id,
  });
  const [createChildComponent, openCreatePresetSidePage] = useCreatePreset({
    presetKey,
    isAddPresetWithGroup,
    screenData,
    titleDialogAdd,
  });
  const handleUpdatePreset = () => {
    onUpdatePreset(activePreset);
  };
  const handleDeletePreset = () => {
    onDeletePreset(activePreset);
  };
  const handleSharePreset = () => {
    onSharePreset(activePreset);
  };
  const handleUnsharePreset = () => {
    onUnsharePreset(activePreset);
  };

  const handleRenamePreset = () => {
    onRenamePreset(activePreset);
  };
  const handleCreatePreset = async () => {
    await openCreatePresetSidePage();
  };

  const handleSaveAs = async () => {
    if (activePreset) {
      await openCreatePresetSidePage({
        data: activePreset.data,
        group: activePreset.group,
        title: `${activePreset.title} (copy)`,
      });
    } else {
      await openCreatePresetSidePage();
    }
  };

  const handleResetPreset = async () => {
    await onResetPreset({ preset: activePreset });
  };

  return (
    <React.Fragment>
      <ButtonGroup>
        <Button
          variant={'outlined'}
          tooltip={'Open presets'}
          sx={{ whiteSpace: 'nowrap' }}
          icon={
            <Badge
              badgeContent={4}
              color={'error'}
              variant={'dot'}
              invisible={!isChanged}
            >
              <ArticleIcon fontSize={'inherit'} />
            </Badge>
          }
          onClick={onOpenPresetsList}
        >
          {activePreset ? `Preset: ${activePreset?.title}` : 'Presets'}
        </Button>
        <Permissions actions={actions}>
          {({ isActionAllowed }) =>
            activePreset ? (
              <Menu
                anchor={
                  <Button tooltip={'Preset actions'}>
                    <MoreVertIcon />
                  </Button>
                }
              >
                {isActionAllowed(ACTION_RESET_PRESET) ? (
                  <MenuItem onClick={handleResetPreset}>Reset</MenuItem>
                ) : null}
                {isActionAllowed(ACTION_UPDATE_PRESET) ? (
                  <MenuItem onClick={handleUpdatePreset}>Update</MenuItem>
                ) : null}
                {isActionAllowed(ACTION_RESET_PRESET) ||
                isActionAllowed(ACTION_UPDATE_PRESET) ? (
                  <Divider />
                ) : null}

                {isActionAllowed(ACTION_DUPLICATE_PRESET) ? (
                  <MenuItem onClick={handleSaveAs}>Save as</MenuItem>
                ) : null}

                {isActionAllowed(ACTION_UNSHARE_PRESET) ? (
                  <MenuItem onClick={handleUnsharePreset}>Unshare</MenuItem>
                ) : null}
                {isActionAllowed(ACTION_SHARE_PRESET) ? (
                  <MenuItem onClick={handleSharePreset}>Share</MenuItem>
                ) : null}
                {isActionAllowed(ACTION_RENAME_PRESET) ? (
                  <MenuItem onClick={handleRenamePreset}>Rename</MenuItem>
                ) : null}
                {isActionAllowed(ACTION_DELETE_PRESET) ? (
                  <MenuItem onClick={handleDeletePreset}>Delete</MenuItem>
                ) : null}
              </Menu>
            ) : (
              <Button tooltip={'Add preset'} onClick={handleCreatePreset}>
                <AddIcon />
              </Button>
            )
          }
        </Permissions>
      </ButtonGroup>
      {createChildComponent}
    </React.Fragment>
  );
};

export default PresetsPreviewButton;
